import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { Version, Widget } from "../../interfaces";
import { WidgetCreator } from "../../components/widgets/WidgetCreator";
import { useVersions } from "../../hooks/useVersions";
import { useWidgets } from "../../hooks/useWidgets";
import { AppContext } from "../../context/index";
import { Column } from 'primereact/column';
import { Button, InputText } from "iuvity-ui-components";
import { Controller, useForm } from "react-hook-form";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { MultiSelect } from 'primereact/multiselect';
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const filterDefaultValues = {
  widgetname: "",
  description: "",
  releaseversion: ""
};

export const AdminWidgetsPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, user, pageFilter, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          widgetname: pageFilter?.widgetname || "",
          description: pageFilter?.description || "",
          releaseversion: pageFilter?.releaseversion || "",
        }
      : filterDefaultValues,
  });

  const { control, setValue } = form; 

  const { data, widgetAction, reloadFn } = useWidgets("DATA");
  const { data: versionOptions } = useVersions("DATA");
  const [modalProps, setModalProps] = useState<{
    visible: boolean;
    type: "CREATE" | "UPDATE" | null;
  }>({
    visible: false,
    type: null,
  });
  const [auxModal, auxModalController] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "widgetname",
          header: t("Widgets.widgetnameLabel"),
        },
        {
          field: "description",
          header: t("Widgets.descriptionLabel"),
        },
        {
          field: "releaseversion",
          header: t("Widgets.releaseversionLabel"),
        },
        {
          field: "actions",
          header: t("Widgets.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    widgetname: { value: pageFilter?.widgetname, matchMode: FilterMatchMode.IN },
    description: { value: pageFilter?.description, matchMode: FilterMatchMode.CONTAINS },
    releaseversion: { value: pageFilter?.releaseversion, matchMode: FilterMatchMode.IN },
  };

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);

  const actionDeleteFn = async (widget: Widget) => {
    await widgetAction("DELETE", widget.id);
    reloadFn();
  };

  const onShowModal = (type: "CREATE" | "UPDATE", widget?: Widget) => {
    if (widget) {
      setAppState((prev) => ({
        ...prev,
        selectedWidget: widget,
      }));
    }
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      type,
    }));
  };

  const handleModalProps = async (widget: Widget) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("Widgets.deleteModalTitle"),
      content: `${t("Widgets.deleteModalContent1")} "${widget?.widgetname}" ${t(
        "Widgets.deleteModalContent2"
      )}`,
      onConfirm: () => actionDeleteFn(widget),
    }));
  };
  
  const onClearFiltersColumn = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(filterDefaultValues);
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Widgets.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`Widgets.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`Widgets.${options.field}PlaceholderLabel`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    const arrayOriginal = data
      ?.map((item: any) => (
        {
          label: item[`${options.field}`], 
          value: item[`${options.field}`]
        }))
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
          name={options.field}
          control={control}
          // rules={PermissionsFormSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Widgets.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Widgets.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("Widgets.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.applyFilterLabel")}
        className="p-button"
        onClick={()=>{
          options.filterApplyCallback();
          setAppState((prev: any)=>({
            ...prev,
            // pageFilter: getValues(),
          }));
        }}
      />
    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFiltersColumn}
      />
    )
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("Widgets.widgetTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Widgets.widgetTitle"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Widgets.widgetDescription")}</span>
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Widgets.newWidgetTitle"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => onShowModal("CREATE"),
          },
        ])}
      />

      <BoxFull className="dt-c my-4">
        {data ? (
          <DataTable
            header={
              <TableHeader
                title={t("Widgets.widgetTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            editMode="row"
            dataKey="id"
            sortField="functionality"
            sortOrder={1}
            filters={filters}
            value={data.map((widget: Widget) => ({
              ...widget,
              actions: (
                <Actions
                  enableDelete={!!widget.createdby}
                  onEdit={() => onShowModal("UPDATE", widget)}
                  onDelete={() => handleModalProps(widget)}
                  visibleObject={{ ...permissiondInfoByRole }}
                />
              ),
            }))}
            scrollable
            resizableColumns
            globalFilterFields={['widgetname', 'description', 'releaseversion']}
            filterDisplay="menu"
            stateStorage="session"
            stateKey="filters-table"
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? {
                      justifyContent: "center",
                      display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                    }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field === "actions") ? "8.3rem" : "14rem",
                  display: (col.field === "actions")
                    ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                    : undefined,
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={col.field === "actions" ? false : true}
                filterElement={
                  ((col.field === "description") && filterTemplate) ||
                  ((col.field === "widgetname" || col.field === "releaseversion") && multiselectFilterTemplate)
                }
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby')}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      {modalProps.visible && versionOptions && (
        <WidgetCreator
          visible={modalProps.visible}
          setModalProps={setModalProps}
          update={modalProps.type === "UPDATE"}
          createFn={widgetAction}
          reloadFn={reloadFn}
          auxModalController={auxModalController}
          options={versionOptions.map((ver: Version) => ({
            label: ver?.name,
            value: ver?.name,
          }))}
        />
      )}

      <CustomDialog
        title={auxModal.title}
        visible={auxModal.visible}
        content={auxModal.content}
        handleModalState={auxModalController}
        onConfirm={auxModal.onConfirm}
      />
    </>
  );
};
